/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import { types, getEnv } from 'mobx-state-tree';
import SlotsModel from './SlotsModel';
import _ from 'underscore';
import { isAlive } from 'mobx-state-tree';

const OVERLAY_STATE = {
	DISPLAY: 'DISPLAY',
	HIDE: 'HIDE',
	SMALL: 'SMALL'
};

const SUPPORT_SMALL_VIEW_CHARTS = ['Scatter', 'PackedBubble', 'HierarchicalPackedBubble', 'Wordle', 'Treemap', 'Dial', 'Bubble', 'Heatmap'];
const SUPPORT_SMALL_VIEW_CHARTS_WIDE = ['DualAxis'];
const MINIMUM_SIZE = 250;
const SMALL_VIEW_SIZE = 300;
const SMALL_VIEW_WIDE_SIZE = 400;

export default types
	.model('OverlayModel', {
		slotsModel: SlotsModel,
		modelVer: types.optional(types.number, 0)
	})
	.volatile(() => ({
		content: null,
		dashboardApi: null,
		overlayState: ''
	}))
	.views((self) => ({
		/**
		 * @return {array} slot apis
		 */
		getSlotInfo() {
			if (self.slotsModel && isAlive(self.slotsModel)) {
				return self.slotsModel.getSlotInfo();
			} else {
				return [];
			}
		},
		/**
		 * @param {Object} slot - slot api
		 * @return {Object} - SlotDataItemHandler
		 */
		getSlotDataItemHandler(slot, indexInSlot) {
			if (self.slotsModel) {
				return self.slotsModel.getSlotDataItemHandler(slot, indexInSlot);
			}
		},

		get OVERLAY_STATE() {
			return OVERLAY_STATE;
		}
	}))
	.actions((self) => {
		const listeners = [];

		/**
		 * @param {String} layerId
		 * @returns true/false to hide/show layers
		 */
		const _isHideLayer = function (visualization, layerId) {
			const visDefinition = visualization.getDefinition();
			const datasets = visDefinition.getDatasetList() || [];
			const layer = datasets.find((dataset) => {
				return dataset.id === layerId;
			}) || {};
			return !layer.caption;
		};

		/**
		* returns an array of objects that describe slots, the views and layers they're associated with,
		* TODO: In future, this function can be extended to include items that are 'context' (dataItems in a view not mapped to a slot)
		* @param visualization visualization api
		* @returns an array of Slot/View information objects by view (or layer depending on how you think of it!)
		*/
		const _getSlotInfoForEachView = function (visualization) {
			const slots = visualization.getSlots().getSlotList();
			const slotDefinitions = visualization.getDefinition().getSlotList();
			const sharedSlots = slotDefinitions.filter(slotDefn => slotDefn.getDatasetIdList().length > 1);
			const slotAndViewInfoArray = [];
			if (sharedSlots.length > 0) {
				// single UI layer
				const defnLayerId = 'data';
				slotAndViewInfoArray.push(
					{
						slotAPIs: slots || [],
						layerId: defnLayerId,
						layerCaption: null,
						isHideLayer: _isHideLayer(visualization, defnLayerId)
					}
				);
			} else {
				const slotsGroupedByLayer = _.groupBy(slots, slot => slot.getDefinition().getDatasetIdList()[0]);
				//Ensure an array of slots arrays is returned in definition order.
				visualization.getDefinition().getDatasetList().forEach((dataset, index) => {
					const slotsByDataset = slotsGroupedByLayer[dataset.id];
					slotAndViewInfoArray.push(
						{
							slotAPIs: slotsByDataset || [],
							layerId: dataset.id,
							layerCaption: dataset.caption || dataset.id,
							isHideLayer: _isHideLayer(visualization, dataset.id),
							open: index === 0
						}
					);
				});
			}
			return slotAndViewInfoArray;
		};

		let _debouncedUpdateModel = null;
		let _onSetup = null;
		let _onModelChange = null;

		return {
			afterCreate() {
				// @todo: STOP using debounce
				// SHOULD use transaction feature instead
				// Event handling of selection and event handling for mapping change needs to be seprated.
				// Combining the two, results in unnecessary on/off upon mapping.
				// Added [setupEvents] to distinguish the two cases for now.
				_debouncedUpdateModel = _.debounce((setupEvents) => {
					if (isAlive(self)) {
						self._updateModel(setupEvents);
					}
				});
				// The different usage of _debouncedUpdateModel handler
				// 1. handler for content selection change.
				_onSetup = _debouncedUpdateModel.bind(this, true);
				// 2. hander for slots mapping change.
				_onModelChange = _debouncedUpdateModel.bind(this, false);

				self.content = getEnv(self).content;
				self.dashboardApi = getEnv(self).dashboardApi;
				if (self.slotsModel) {
					self.slotsModel.setDashboardApi(self.dashboardApi);
				}
				_onSetup();
			},
			beforeDestroy() {
				listeners.forEach(listener => listener.remove());
			},
			/**
			 * Set state of overlay
			 * if display is falsy, the overlay will hidden
			 * if display is truthy, the model computes if the container has enough space for a full, small or hidden view
			 * @param {boolean} display 
			 */
			setOverlayState(display) {
				if (display) {
					self.overlayState = self._getSizeState();
				} else {
					self.overlayState = OVERLAY_STATE.HIDE;
				}
			},
			/** decides which view state for overlay
			 * a) focus mode: always show the overlay
			 * b) charts that are smaller than MINIMUM_SIZE in width or height, hide the overlay
			 * c) charts that support a smaller view, determine if the state is small or full based on the chart type
			 */
			_getSizeState() {
				let sizeState = OVERLAY_STATE.DISPLAY;
				const contentViewDOM = self.content.getFeature('ContentViewDOM');
				const visualization = self.content.getFeature('Visualization');
				const chartType = visualization.getType();
				if (contentViewDOM) {
					const contentNode = contentViewDOM.getNode();
					const dashboardState = self.dashboardApi.getFeature('DashboardState');
					const state = self.content.getFeature('state');
					// hide overlay when in consumption mode or content has an error
					if(!dashboardState.getUiState().authoring || (state && state.getError()) || visualization.hasUnavailableMetadataColumns()){
						sizeState = OVERLAY_STATE.HIDE;
					} else if (dashboardState.getUiState().focus) {
						sizeState = OVERLAY_STATE.DISPLAY;
					} else if (contentNode.offsetWidth < MINIMUM_SIZE || contentNode.offsetHeight < MINIMUM_SIZE) {
						sizeState = OVERLAY_STATE.HIDE;
					} else if (SUPPORT_SMALL_VIEW_CHARTS.includes(chartType)) {
						sizeState = contentNode.offsetWidth < SMALL_VIEW_SIZE ? OVERLAY_STATE.SMALL : OVERLAY_STATE.DISPLAY;
					} else if (SUPPORT_SMALL_VIEW_CHARTS_WIDE.includes(chartType)) {
						sizeState = contentNode.offsetWidth < SMALL_VIEW_WIDE_SIZE ? OVERLAY_STATE.SMALL : OVERLAY_STATE.DISPLAY;
					}
				}
				return sizeState;
			},
			_updateModel(setupEvents) {
				self._setOverlayState();
				self._setSlots(setupEvents);
			},

			_setOverlayState() {
				const visualization = self.content.getFeature('Visualization');
				if (visualization) {
					try {
						const isMappingComplete = visualization.getSlots().isMappingComplete();
						self.overlayState = isMappingComplete ? OVERLAY_STATE.HIDE : self._getSizeState();
					} catch(e) {
						self.overlayState = OVERLAY_STATE.HIDE;
					}
				} else {
					self.overlayState = OVERLAY_STATE.HIDE;
				}
			},

			_setSlots(setupEvents) {
				let slotInfo = [];
				if (self.overlayState === OVERLAY_STATE.DISPLAY) {
					const visualization = self.content.getFeature('Visualization');
					if (visualization) {
						slotInfo = _getSlotInfoForEachView(visualization) || [];
					}
				}
				if (self.slotsModel && isAlive(self.slotsModel)) {
					if (setupEvents) {
						self.slotsModel.clear();
						self.slotsModel.setContent(self.content);
						self.slotsModel.setVisualization();
						self.slotsModel.setUpEvents(_onModelChange);
					}
					self.slotsModel.setSlotInfo(slotInfo);
					self.modelVer++;
				}
			}
		};
	});



// WEBPACK FOOTER //
// ./src/js/models/OverlayModel.js